/*
eWorldES Proprietary Software
Copyright (C) 2025 eWorldES. All rights reserved.

This software is proprietary to eWorldES and is protected by copyright laws and international treaties.
You may not use, copy, modify, distribute, or disclose this software without explicit written permission from eWorldES.
Any unauthorized use or reproduction of this software is strictly prohibited and may result in legal action.

License Agreement:
By using this software, you agree to be bound by the terms of the eWorldES Proprietary Software License Agreement.
For a copy of the agreement, visit https://eworldes.com/ or contact eWorldES directly for more information.

This software is provided "AS IS" without warranty of any kind, express or implied, including but not limited to the warranties of
merchantability, fitness for a particular purpose, or non-infringement. In no event shall eWorldES be liable for any damages
arising out of the use of this software, even if eWorldES has been advised of the possibility of such damages.

For inquiries or requests regarding this software, please contact eWorldES at:
Email: info@eworldes.com
Website: https://eworldes.com/
*/

import React from "react";
import { Controller, Control, FieldErrors } from "react-hook-form";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { FormControl, FormHelperText } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

export type BaseDatePickerProps = {
  name: string;
  control: Control<any>;
  label: string;
  rules?: Record<string, any>;
  required?: boolean;
  errors?: FieldErrors<any>;
  helperText?: string;
  disablePast?: boolean;
  disableFuture?: boolean;
  fullWidth?: boolean;
  minDate?: string | number | Date | Dayjs | null;
  disabled?: boolean; 
};

const DatePickerComp: React.FC<BaseDatePickerProps> = ({
  name,
  control,
  label,
  required,
  errors,
  helperText, 
  disableFuture,
  disablePast,
  fullWidth = false,
  minDate,
  disabled,
  rules = {},
}) => {
  const error = Boolean(errors?.[name]);

  // Enhance rules based on disableFuture/disablePast
  const enhancedRules = {
    ...rules,
    validate: (value: string | null) => {
      if (!value) return true;

      const selected = dayjs(value, "MM/DD/YYYY");
      const today = dayjs().startOf("day");

      if (disableFuture && selected.isAfter(today)) {
        return "Future dates are not allowed";
      }
      if (disablePast && selected.isBefore(today)) {
        return "Past dates are not allowed";
      }

      return true;
    },
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <FormControl required={required} error={error} fullWidth={fullWidth}>
        <Controller
          name={name}
          control={control}
          rules={enhancedRules}
          render={({ field }) => (
            <DatePicker
              {...field}
              disableFuture={disableFuture}
              disablePast={disablePast}
              label={label}
              disabled={disabled}
              value={field.value ? dayjs(field.value, "MM/DD/YYYY") : null}
              onChange={(date: Dayjs | null) =>
                field.onChange(date ? dayjs(date).format("MM/DD/YYYY") : null)
              }
              slotProps={{ textField: { error, required } }}
              sx={{
                
                "& .MuiInputLabel-asterisk": {
                  color: "#F44336",
                },
              }}
              minDate={minDate !== null && minDate !== undefined ? dayjs(minDate) : undefined}
            />
          )}
        />
        {error && helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    </LocalizationProvider>
  );
};

export default DatePickerComp;

